import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Car,
  AlertCircle,
  DollarSign,
  Star,
  Award,
  Clock,
  Target,
  Zap,
  Trophy,
  Medal,
  ChevronUp,
  ChevronDown
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-500" size={20} /> : <ChevronDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const DriverPerformanceReport = () => {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [driverAnalytics, setDriverAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("7"); // days
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("totalRevenue");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedSections, setExpandedSections] = useState({});

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data using new driver analytics API
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch drivers first
      const driversRes = await fetch(`${API_URL}/driver/`);
      const driversData = await driversRes.json();
      setDrivers(driversData || []);

      // Fetch comprehensive analytics for each driver
      const driverAnalyticsPromises = driversData.map(async (driver) => {
        try {
          const analyticsRes = await fetch(`${API_URL}/driver/${driver.driver_id}/comprehensive_stats`);
          const analyticsData = await analyticsRes.json();
          
          if (analyticsRes.ok) {
            return {
              driverId: driver.driver_id,
              driverInfo: driver,
              analytics: analyticsData
            };
          }
        } catch (error) {
          console.error(`Error fetching analytics for driver ${driver.driver_id}:`, error);
        }
        return null;
      });

      const analyticsResults = await Promise.all(driverAnalyticsPromises);
      const validAnalytics = analyticsResults.filter(result => result !== null);

      console.log("Driver performance report - Analytics data:", validAnalytics);

      // Set analytics data for processing
      setDriverAnalytics(validAnalytics);
      
      // Still fetch trips for additional analysis if needed
      const tripsRes = await fetch(`${API_URL}/trip/`);
      const tripsData = await tripsRes.json();
      setTrips(tripsData || []);
      
      const customersRes = await fetch(`${API_URL}/customerdetails/`);
      const customersData = await customersRes.json();
      setCustomers(customersData || []);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process driver performance data using new analytics API
  const driverAnalysis = useMemo(() => {
    if (!driverAnalytics.length) {
      return {
        filteredTrips: [],
        privateTrips: [],
        rideshareTrips: [],
        driverPerformance: {},
        totalRevenue: 0
      };
    }

    console.log(`Processing ${driverAnalytics.length} drivers with analytics data`);

    // Transform analytics data into the expected format
    const driverPerformance = {};

    driverAnalytics.forEach(driverData => {
      const { driverId, driverInfo, analytics } = driverData;
      
      console.log(`Processing driver ${driverId}:`, driverInfo);
      console.log(`Analytics data:`, analytics);
      console.log(`DriverId value:`, driverId);
      console.log(`DriverInfo.name:`, driverInfo?.name);
      
      if (!analytics || !analytics.trip_statistics) {
        console.warn(`No trip_statistics data available for driver ${driverId}`);
        return;
      }

      const { trip_statistics, earnings, performance, activity, ratings } = analytics;
      
      console.log(`Trip statistics:`, trip_statistics);
      console.log(`Earnings:`, earnings);
      
      // Additional safety check for ratings object
      if (!ratings) {
        console.warn(`No ratings data available for driver ${driverId}`);
        ratings = {};
      }

      // Use driver_id from analytics if driverId is undefined
      const actualDriverId = driverId || analytics.driver_id;
      console.log(`Actual driverId being used:`, actualDriverId);

      driverPerformance[actualDriverId] = {
        driverId: actualDriverId,
        driverInfo: driverInfo,
        // Trip statistics from analytics
        totalTrips: trip_statistics.combined?.total_trips || 0,
        privateTrips: trip_statistics.private?.completed || 0,
        rideshareTrips: trip_statistics.rideshare?.completed || 0,
        
        // Earnings from analytics
        totalRevenue: earnings?.combined?.total_earnings || 0,
        privateRevenue: earnings?.private?.total_earnings || 0,
        rideshareRevenue: earnings?.rideshare?.total_earnings || 0,
        avgRevenue: earnings?.combined?.average_per_trip || 0,
        avgPrivateRevenue: earnings?.private?.average_per_trip || 0,
        avgRideshareRevenue: earnings?.rideshare?.average_per_trip || 0,
        
        // Performance metrics from analytics
        completionRate: performance?.completion_rate || 0,
        avgDistance: performance?.average_distance_per_trip || 0,
        totalDistance: performance?.total_distance_km || 0,
        avgTripDuration: performance?.average_trip_duration_minutes || 0,
        
        // Activity metrics from analytics
        activeDays: activity?.days_active || 0,
        avgTripsPerDay: activity?.average_trips_per_day || 0,
        currentStreak: activity?.current_streak_days || 0,
        longestStreak: activity?.longest_streak_days || 0,
        lastActiveDate: activity?.last_active_date,
        
        // Ratings from analytics
        rating: ratings?.received?.combined?.average_received || 0,
        ratingCount: (ratings?.received?.private?.total_received || 0) + (ratings?.received?.rideshare?.total_received || 0),
        
        // Additional fields for compatibility
        uniqueCustomers: new Set(), // Could be enhanced with customer data
        tripsByHour: {}, // Could be enhanced with trip timing data
        tripsByDay: {}, // Could be enhanced with trip day data
        firstTripDate: null, // Could be derived from activity data
        lastTripDate: activity?.last_active_date
      };
    });

    // Calculate totals from processed data
    const totalRevenue = Object.values(driverPerformance).reduce((sum, driver) => sum + (driver.totalRevenue || 0), 0);

    return {
      filteredTrips: [], // Could be enhanced with actual trip filtering
      privateTrips: [], // Could be enhanced with actual private trips
      rideshareTrips: [], // Could be enhanced with actual rideshare trips
      driverPerformance,
      totalRevenue
    };
  }, [driverAnalytics]);

  // Get top performers
  const getTopPerformers = (metric, limit = 20) => {
    if (!driverPerformance || Object.keys(driverPerformance).length === 0) {
      return [];
    }
    return Object.values(driverPerformance)
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, limit);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename, includeDetails = false) => {
    const headers = includeDetails 
      ? ['Driver ID', 'Name', 'Email', 'Phone', 'Total Trips', 'Private Trips', 'Rideshare Trips', 'Total Revenue', 'Avg Revenue', 'Rating', 'Performance Score', 'Unique Customers', 'Active Days']
      : ['Driver', 'Value'];
    
    const csv = [
      headers,
      ...data.map(driver => {
        if (includeDetails) {
          const info = driver.driverInfo;
          return [
            driver.driverId,
            info?.name || `${info?.first_name || ''} ${info?.last_name || ''}`.trim() || `Driver #${driver.driverId}`,
            info?.email || 'N/A',
            info?.phone || 'N/A',
            driver.totalTrips,
            driver.privateTrips,
            driver.rideshareTrips,
            driver.totalRevenue?.toFixed(2) || 0,
            driver.avgRevenue?.toFixed(2) || 0,
            driver.rating?.toFixed(1) || 0,
            driver.performanceScore || 0,
            driver.uniqueCustomersCount || 0,
            driver.activeDaysCount || 0
          ];
        } else {
          const info = driver.driverInfo;
          const name = info?.name || `${info?.first_name || ''} ${info?.last_name || ''}`.trim() || `Driver #${driver.driverId}`;
          return [name, driver[Object.keys(driver)[1]]?.toFixed(2) || 0];
        }
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const { 
    filteredTrips, 
    privateTrips, 
    rideshareTrips, 
    driverPerformance, 
    totalRevenue 
  } = driverAnalysis;

  const topDriversByTrips = getTopPerformers('totalTrips');
  const topDriversByRevenue = getTopPerformers('totalRevenue');
  const topDriversByRating = getTopPerformers('rating');
  const topDriversByScore = getTopPerformers('performanceScore');

  const activeDrivers = Object.values(driverPerformance).filter(d => d.totalTrips > 0).length;
  const avgDriverRevenue = activeDrivers > 0 ? totalRevenue / activeDrivers : 0;

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Driver Performance Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive driver analysis including performance metrics, rankings, and productivity insights
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Driver Performance Report - Business Value & Features">
          <div className="space-y-3">
            <div>
              <strong className="text-blue-900">👥 Report Overview:</strong>
              <p>Comprehensive driver analysis including performance metrics, rankings, productivity insights, and composite performance scoring.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Driver Management:</strong> Who are my best drivers? Which drivers need training? Who generates most revenue?</li>
                <li><strong>Performance Optimization:</strong> Who should I reward? How consistent are my drivers? Who needs attention?</li>
                <li><strong>Service Analysis:</strong> How do drivers perform on private vs rideshare? Who serves most customers?</li>
                <li><strong>Strategic Planning:</strong> What's the overall driver performance? How do drivers compare? Who should I promote?</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">✅ Key Features:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Performance Scoring:</strong> 0-100 composite score based on trips, revenue, rating, customers, and consistency</li>
                <li><strong>Smart Rankings:</strong> Top drivers by performance score, trips, revenue, and customer ratings</li>
                <li><strong>Service Breakdown:</strong> Private vs rideshare performance comparison for each driver</li>
                <li><strong>Performance Distribution:</strong> Categories: Excellent (80-100), Good (60-79), Average (40-59), Needs Improvement (0-39)</li>
                <li><strong>Detailed Metrics:</strong> Active days, unique customers, average revenue, service type breakdowns</li>
                <li><strong>Export Functionality:</strong> CSV download for all driver performance data</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">💼 Business Impact:</strong>
              <p>This report provides immediate management value by helping you identify top performers for rewards, recognize drivers needing support, optimize driver assignments, improve customer satisfaction through driver quality, and maximize revenue through driver optimization.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🏆 Performance Score Algorithm:</strong>
              <p>Composite score calculation: Trip Volume (30%), Revenue Generation (25%), Customer Rating (20%), Customer Reach (15%), Consistency (10%). This weighted approach ensures balanced performance evaluation.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🔍 Data Source:</strong>
              <p>Uses REAL data from live API calls to trips, drivers, and customers endpoints. All performance metrics are calculated from actual completed trips with real revenue and customer rating data.</p>
            </div>
          </div>
        </ReportTooltip>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Drivers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {activeDrivers}
                </p>
              </div>
              <Users className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${(totalRevenue || 0).toFixed(0)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Driver Revenue</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ${(avgDriverRevenue || 0).toFixed(0)}
                </p>
              </div>
              <Target className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredTrips.length}
                </p>
              </div>
              <Activity className="text-gray-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Rating</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {topDriversByRating.length > 0 ? ((topDriversByRating.reduce((sum, d) => sum + (d.rating || 0), 0) / topDriversByRating.length).toFixed(1)) : '0.0'}
                </p>
              </div>
              <Star className="text-yellow-500" size={24} />
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-yellow-800 mb-2">Debug Info:</h3>
          <p className="text-xs text-yellow-700">Filter: {getFilterLabel(timeFilter)}</p>
          <p className="text-xs text-yellow-700">Total Drivers: {drivers.length} | Active Drivers: {activeDrivers}</p>
          <p className="text-xs text-yellow-700">Total Trips: {trips.length} | Filtered: {filteredTrips.length}</p>
          <p className="text-xs text-yellow-700">Private Trips: {privateTrips.length} | Rideshare Trips: {rideshareTrips.length}</p>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Top Performance Score */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Trophy className="mr-2 text-yellow-500" />
                Top Drivers by Performance Score
              </div>
              <button
                onClick={() => exportData(topDriversByScore, 'top-drivers-performance.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Performance Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customers</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topDriversByScore.map((driver, index) => (
                      <tr key={driver.driverId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                           `Driver #${driver.driverId}` || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-yellow-500 h-2 rounded-full" 
                                style={{ width: `${driver.performanceScore}%` }}
                              ></div>
                            </div>
                            <span className="font-bold">{(driver.performanceScore || 0).toFixed(2)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.totalTrips}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${(driver.totalRevenue || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <Star className="text-yellow-500 mr-1" size={14} />
                            {(driver.rating || 0).toFixed(1)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.uniqueCustomersCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Drivers by Trips */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Activity className="mr-2 text-blue-500" />
                Top Drivers by Trip Count
              </div>
              <button
                onClick={() => exportData(topDriversByTrips, 'top-drivers-trips.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topDriversByTrips.slice(0, 9).map((driver, index) => (
                    <div key={driver.driverId} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Medal className="text-blue-500" size={20} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                             `Driver #${driver.driverId}`}
                          </span>
                        </div>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Total Trips: {driver.totalTrips}</p>
                        <p>Private: {driver.privateTrips} | Rideshare: {driver.rideshareTrips}</p>
                        <p>Revenue: ${(driver.totalRevenue || 0).toFixed(2)}</p>
                        <p>Avg: ${(driver.avgRevenue || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Top Drivers by Revenue */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <DollarSign className="mr-2 text-green-500" />
                Top Drivers by Revenue
              </div>
              <button
                onClick={() => exportData(topDriversByRevenue, 'top-drivers-revenue.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topDriversByRevenue.slice(0, 9).map((driver, index) => (
                    <div key={driver.driverId} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Award className="text-green-500" size={20} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                             `Driver #${driver.driverId}`}
                          </span>
                        </div>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Revenue: ${(driver.totalRevenue || 0).toFixed(2)}</p>
                        <p>Total Trips: {driver.totalTrips}</p>
                        <p>Avg Revenue: ${(driver.avgRevenue || 0).toFixed(2)}</p>
                        <p>Private: ${(driver.privateRevenue || 0).toFixed(2)} | Rideshare: ${(driver.rideshareRevenue || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Driver Performance Distribution */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <BarChart3 className="mr-2 text-purple-500" />
              Driver Performance Distribution
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Excellent', min: 80, max: 100, color: 'green', drivers: Object.values(driverPerformance).filter(d => d.performanceScore >= 80) },
                    { label: 'Good', min: 60, max: 79, color: 'blue', drivers: Object.values(driverPerformance).filter(d => d.performanceScore >= 60 && d.performanceScore < 80) },
                    { label: 'Average', min: 40, max: 59, color: 'yellow', drivers: Object.values(driverPerformance).filter(d => d.performanceScore >= 40 && d.performanceScore < 60) },
                    { label: 'Needs Improvement', min: 0, max: 39, color: 'red', drivers: Object.values(driverPerformance).filter(d => d.performanceScore < 40) }
                  ].map(category => (
                    <div key={category.label} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {category.label}
                        </span>
                        <span className={`bg-${category.color}-100 text-${category.color}-800 px-2 py-1 rounded text-sm font-medium`}>
                          {category.drivers.length}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Score: {category.min}-{category.max}</p>
                        <p>Percentage: {activeDrivers > 0 ? ((category.drivers.length / activeDrivers) * 100).toFixed(1) : 0}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Driver Metrics */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Users className="mr-2 text-indigo-500" />
                Detailed Driver Metrics
              </div>
              <button
                onClick={() => exportData(Object.values(driverPerformance), 'detailed-driver-metrics.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export All</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Performance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customers</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Active Days</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {Object.values(driverPerformance)
                      .sort((a, b) => b.performanceScore - a.performanceScore)
                      .slice(0, 20)
                      .map((driver) => (
                      <tr key={driver.driverId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                           `Driver #${driver.driverId}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full" 
                                style={{ width: `${driver.performanceScore}%` }}
                              ></div>
                            </div>
                            <span className="font-medium">{driver.performanceScore}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.totalTrips} <span className="text-gray-500">({driver.privateTrips}P/{driver.rideshareTrips}R)</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${(driver.totalRevenue || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <Star className="text-yellow-500 mr-1" size={14} />
                            {(driver.rating || 0).toFixed(1)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.uniqueCustomersCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.activeDaysCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DriverPerformanceReport;
